import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'

import ClaimReferralButton from '@/components/layouts/ReferralInviteLayout/components/ClaimReferralButton'
import { useHasMultipleReferralInvites } from '@/components/layouts/ReferralInviteLayout/hooks/useHasMultipleReferralInvites'
import { useRecordInteractionMutation } from '@/components/layouts/ReferralInviteLayout/hooks/useRecordInteractionMutation'
import { useReferralsTerm } from '@/components/layouts/ReferralInviteLayout/hooks/useReferralsTerm'
import HRMessage from '@/components/misc/HRMessage'
import { useClaimReferralModal } from '@/components/misc/ReferralDetails/hooks/useClaimReferralModal'
import SelectUserCompany, {
  CompanyOption,
  SelectUserCompanyOption,
} from '@/components/select/SelectUserCompany'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Heading } from '@/components/ui/heading'
import { Spinner } from '@/components/ui/spinner'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import { useUserCompanies } from '@/hooks/useUserCompanies'
import { faPlus, faTimes } from '@/lib/fontAwesomePro/regular'
import { ROUTES } from '@/lib/routes'

interface ClaimOption {
  heading: string
  content: React.ReactNode
}

const AlreadyLoggedInAsCompanyUserModal: React.FC = () => {
  const claimReferralModal = useClaimReferralModal()
  const referralsTerm = useReferralsTerm()
  const currentCompany = useCurrentCompanyOrFail()
  const companies = useUserCompanies()
  const currentCompanyName = currentCompany.presentationName
  const navigate = useNavigate()
  const hasMultipleInvites = useHasMultipleReferralInvites()
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(
    null,
  )

  const recordInteractionMutation = useRecordInteractionMutation()
  const isRecordingInteraction = recordInteractionMutation.isPending

  async function onHide() {
    await recordInteractionMutation.mutateAsync()
    claimReferralModal.hide()
  }

  const defaultValue: SelectUserCompanyOption | null = selectedCompany
    ? {
        label: selectedCompany.presentationName,
        value: selectedCompany.id,
        isDisabled: false,
      }
    : null

  function buildOptions(): ClaimOption[] {
    const options: ClaimOption[] = [
      {
        heading: `Claim ${referralsTerm} for ${currentCompanyName}`,
        content: (
          <>
            <p className="mb-2">
              Click the button below to claim {referralsTerm} for{' '}
              {currentCompanyName}.
            </p>

            <ClaimReferralButton
              companyIri={currentCompany['@id']}
              variant={selectedCompany ? 'primary' : 'success'}
            >
              Claim {referralsTerm} for {currentCompanyName}
            </ClaimReferralButton>
          </>
        ),
      },
    ]

    if (companies.length > 1) {
      options.push({
        heading: `Claim ${referralsTerm} for another company`,
        content: (
          <>
            <div className="mb-2">
              <p className="mb-2">
                If you want to claim {referralsTerm} for another of your
                companies, please select them below:
              </p>

              <SelectUserCompany
                placeholder="Select company"
                isClearable
                defaultValue={defaultValue}
                onChange={(company) => {
                  setSelectedCompany(company)
                }}
              />

              {selectedCompany && (
                <ClaimReferralButton
                  companyIri={selectedCompany['@id']}
                  className="mt-2"
                  variant="success"
                >
                  Claim {referralsTerm} for {selectedCompany.presentationName}
                </ClaimReferralButton>
              )}
            </div>
          </>
        ),
      })
    }

    options.push({
      heading: `Claim ${referralsTerm} for a new company`,
      content: (
        <>
          <p className="mb-2">
            If you want claim for a company not listed above, please add the
            company first and revisit this link to claim {referralsTerm} for the
            newly added company.
          </p>

          <Button
            variant="secondary"
            disabled={isRecordingInteraction}
            size="lg"
            className="inline-flex w-full items-center justify-center gap-2
              sm:w-auto"
            onClick={async () => {
              await recordInteractionMutation.mutateAsync()
              window.open(ROUTES.addCompany())
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add new company
          </Button>
        </>
      ),
    })

    options.push({
      heading: 'Skip for now',
      content: (
        <>
          <p className="mb-2">
            Click the button below if you want to skip claiming {referralsTerm}{' '}
            for now. You can always revisit this link if you want to claim{' '}
            {referralsTerm}.
          </p>

          <Button
            variant="secondary"
            size="lg"
            className="w-full sm:w-auto"
            disabled={isRecordingInteraction}
            onClick={async () => {
              await recordInteractionMutation.mutateAsync()
              navigate(ROUTES.index())
            }}
          >
            Skip for now
          </Button>
        </>
      ),
    })

    return options
  }

  const options = buildOptions()

  return (
    <Dialog onHide={onHide} open={claimReferralModal.isShowing}>
      <DialogContent showCloseButton={false}>
        <DialogHeader
          className="flex flex-row items-center justify-between gap-2"
        >
          <DialogTitle>
            Claim {hasMultipleInvites ? 'referrals' : 'referral'}
          </DialogTitle>

          {isRecordingInteraction ? (
            <Spinner className="size-5" />
          ) : (
            <FontAwesomeIcon
              icon={faTimes}
              className="hover-dim cursor-pointer"
              style={{ fontSize: '20px' }}
              onClick={onHide}
            />
          )}
        </DialogHeader>

        <DialogBody className="max-h-[70vh]">
          <p className="text-md mb-6">
            You're currently logged in under the company{' '}
            <strong>{currentCompanyName}</strong>. Please confirm how you want
            to proceed.
          </p>

          <div className="flex flex-col gap-4" css={{ h4: { color: 'black' } }}>
            {options.map((option, index) => (
              <>
                <section key={index}>
                  <Heading size={4}>
                    {index + 1}. {option.heading}
                  </Heading>
                  {option.content}
                </section>
                {index !== options.length - 1 && <HRMessage message="OR" />}
              </>
            ))}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

export default AlreadyLoggedInAsCompanyUserModal
